import React, { useState, useEffect } from 'react';
import { Instagram, Send, MessageCircle, Heart, Bookmark, Image, Video, MapPin, Calendar, Users, AlertCircle, CheckCircle, Globe, Bot } from 'lucide-react';

interface InstagramMessage {
  id: string;
  type: 'sent' | 'received' | 'comment' | 'mention';
  sender: string;
  content: string;
  timestamp: Date;
  platform?: 'dm' | 'comment' | 'story';
  postId?: string;
}

interface InstagramConfig {
  accessToken: string;
  businessAccountId: string;
  verifyToken: string;
  chatbotUrl: string;
}

export const InstagramTestDashboard: React.FC = () => {
  const [messages, setMessages] = useState<InstagramMessage[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [testMode, setTestMode] = useState(true);
  const [config, setConfig] = useState<InstagramConfig>({
    accessToken: '',
    businessAccountId: '',
    verifyToken: 'carneiros-paradiso-2024',
    chatbotUrl: 'https://biig.dev/chat?ref=instagram'
  });
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'disconnected' | 'error'>('disconnected');
  const [language, setLanguage] = useState<'pt' | 'en'>('pt');
  const [showConfig, setShowConfig] = useState(false);
  const [isTyping, setIsTyping] = useState(false);

  const API_BASE = 'https://biig-api.b-9f2.workers.dev/api';

  // Portuguese templates for Carneiros Paradiso
  const templates = {
    pt: {
      welcome: '🌴 Olá! Bem-vindo ao Carneiros Paradiso! Como posso ajudar com sua estadia em nosso paraíso?',
      availability: '📅 Para verificar disponibilidade e fazer reservas, acesse nosso assistente virtual: https://biig.dev/chat',
      location: '📍 Estamos localizados na paradisíaca Praia dos Carneiros, Pernambuco. Um dos destinos mais belos do Brasil!',
      amenities: '✨ Oferecemos: Suítes exclusivas, Café da manhã regional, Piscina, Wi-Fi, Experiências personalizadas',
      booking: '🏖️ Reserve agora através do nosso chat: https://biig.dev/chat ou WhatsApp: +55 81 99999-9999',
      thanks: '🌺 Obrigado por escolher Carneiros Paradiso! Mal podemos esperar para recebê-lo!'
    },
    en: {
      welcome: '🌴 Hello! Welcome to Carneiros Paradiso! How can I help with your stay in our paradise?',
      availability: '📅 To check availability and make reservations, visit our virtual assistant: https://biig.dev/chat',
      location: '📍 We are located at the paradisiacal Carneiros Beach, Pernambuco. One of Brazil\'s most beautiful destinations!',
      amenities: '✨ We offer: Exclusive suites, Regional breakfast, Pool, Wi-Fi, Personalized experiences',
      booking: '🏖️ Book now through our chat: https://biig.dev/chat or WhatsApp: +55 81 99999-9999',
      thanks: '🌺 Thank you for choosing Carneiros Paradiso! We can\'t wait to welcome you!'
    }
  };

  useEffect(() => {
    // Load saved config
    const savedConfig = localStorage.getItem('instagram-config');
    if (savedConfig) {
      setConfig(JSON.parse(savedConfig));
    }

    // Check connection status
    checkConnection();

    // Load test messages
    if (testMode) {
      loadMockMessages();
    }
  }, [testMode]);

  const checkConnection = async () => {
    try {
      const endpoint = testMode ? `${API_BASE}/instagram-mock/status` : `${API_BASE}/instagram/test/status`;
      const response = await fetch(endpoint);
      const data = await response.json();
      
      if (testMode || (data.configured?.hasAccessToken && data.configured?.hasBusinessAccount)) {
        setConnectionStatus('connected');
      } else {
        setConnectionStatus('disconnected');
      }
    } catch (error) {
      setConnectionStatus('error');
    }
  };

  const loadMockMessages = () => {
    const mockMessages: InstagramMessage[] = [
      {
        id: '1',
        type: 'received',
        sender: '@turista_feliz',
        content: 'Olá! Vi as fotos no Instagram. Vocês têm disponibilidade para o Carnaval?',
        timestamp: new Date(Date.now() - 3600000),
        platform: 'dm'
      },
      {
        id: '2',
        type: 'sent',
        sender: 'Carneiros Paradiso',
        content: templates.pt.welcome + '\n\n' + templates.pt.availability,
        timestamp: new Date(Date.now() - 3500000),
        platform: 'dm'
      },
      {
        id: '3',
        type: 'comment',
        sender: '@praia_lover',
        content: 'Que lugar incrível! 😍 Como faço reserva?',
        timestamp: new Date(Date.now() - 7200000),
        platform: 'comment',
        postId: 'sunset_photo_123'
      },
      {
        id: '4',
        type: 'sent',
        sender: 'Carneiros Paradiso',
        content: '@praia_lover ' + templates.pt.booking,
        timestamp: new Date(Date.now() - 7100000),
        platform: 'comment'
      }
    ];
    setMessages(mockMessages);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim()) return;

    const newMessage: InstagramMessage = {
      id: Date.now().toString(),
      type: 'sent',
      sender: 'Carneiros Paradiso',
      content: inputMessage,
      timestamp: new Date(),
      platform: 'dm'
    };

    setMessages(prev => [...prev, newMessage]);
    setInputMessage('');

    if (testMode) {
      // Simulate auto-response in test mode
      setIsTyping(true);
      setTimeout(() => {
        const response: InstagramMessage = {
          id: (Date.now() + 1).toString(),
          type: 'received',
          sender: '@customer',
          content: 'Recebi sua mensagem! Vou conferir o link do chat.',
          timestamp: new Date(),
          platform: 'dm'
        };
        setMessages(prev => [...prev, response]);
        setIsTyping(false);
      }, 1500);
    } else {
      // Send via real API
      try {
        await fetch(`${API_BASE}/instagram/test/send-greeting`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            recipientId: 'test_user',
            message: inputMessage
          })
        });
      } catch (error) {
        console.error('Failed to send Instagram message:', error);
      }
    }
  };

  const simulateIncoming = () => {
    const sampleMessages = [
      'Oi! Quais são os preços das suítes?',
      'Hello! Do you have rooms available for next week?',
      'Vocês aceitam pets?',
      'Qual o melhor período para visitar?',
      'Como chego aí saindo de Recife?'
    ];

    const randomMessage = sampleMessages[Math.floor(Math.random() * sampleMessages.length)];
    const incomingMessage: InstagramMessage = {
      id: Date.now().toString(),
      type: 'received',
      sender: `@visitor_${Math.floor(Math.random() * 1000)}`,
      content: randomMessage,
      timestamp: new Date(),
      platform: Math.random() > 0.5 ? 'dm' : 'comment'
    };

    setMessages(prev => [...prev, incomingMessage]);

    // Auto-respond with appropriate template
    setTimeout(() => {
      const isPortuguese = randomMessage.toLowerCase().includes('oi') || 
                          randomMessage.toLowerCase().includes('vocês') ||
                          randomMessage.toLowerCase().includes('qual');
      
      const autoResponse: InstagramMessage = {
        id: (Date.now() + 1).toString(),
        type: 'sent',
        sender: 'Carneiros Paradiso',
        content: isPortuguese ? 
          `${templates.pt.welcome}\n\n${templates.pt.availability}` :
          `${templates.en.welcome}\n\n${templates.en.availability}`,
        timestamp: new Date(),
        platform: incomingMessage.platform
      };
      setMessages(prev => [...prev, autoResponse]);
    }, 1000);
  };

  const sendTemplate = (templateKey: keyof typeof templates.pt) => {
    const template = templates[language][templateKey];
    setInputMessage(template);
  };

  const saveConfig = () => {
    localStorage.setItem('instagram-config', JSON.stringify(config));
    setShowConfig(false);
    checkConnection();
  };

  const testWebhook = async () => {
    try {
      const response = await fetch(`${API_BASE}/instagram/webhook?hub.mode=subscribe&hub.verify_token=${config.verifyToken}&hub.challenge=test_challenge`);
      const result = await response.text();
      if (result === 'test_challenge') {
        alert('✅ Webhook verificado com sucesso!');
      } else {
        alert('❌ Falha na verificação do webhook');
      }
    } catch (error) {
      alert('❌ Erro ao testar webhook');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 p-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full">
                <Instagram className="w-8 h-8 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Carneiros Paradiso - Instagram</h1>
                <p className="text-gray-600">Central de Mensagens e Automação</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Language Toggle */}
              <button
                onClick={() => setLanguage(language === 'pt' ? 'en' : 'pt')}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                <Globe size={20} />
                <span>{language === 'pt' ? 'PT-BR' : 'EN'}</span>
              </button>

              {/* Test Mode Toggle */}
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">Modo Teste</span>
                <button
                  onClick={() => setTestMode(!testMode)}
                  className={`relative w-12 h-6 rounded-full transition-colors ${
                    testMode ? 'bg-purple-500' : 'bg-gray-300'
                  }`}
                >
                  <div className={`absolute w-5 h-5 bg-white rounded-full top-0.5 transition-transform ${
                    testMode ? 'translate-x-6' : 'translate-x-0.5'
                  }`} />
                </button>
              </div>

              {/* Connection Status */}
              <div className="flex items-center gap-2">
                {connectionStatus === 'connected' ? (
                  <>
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <span className="text-sm text-green-600">
                      {testMode ? 'Mock API Pronto' : 'Conectado'}
                    </span>
                  </>
                ) : (
                  <>
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-sm text-red-600">Desconectado</span>
                  </>
                )}
              </div>

              {/* Settings */}
              <button
                onClick={() => setShowConfig(!showConfig)}
                className="px-4 py-2 bg-gray-100 rounded-lg hover:bg-gray-200"
              >
                Configurações
              </button>
            </div>
          </div>
        </div>

        {/* Configuration Panel */}
        {showConfig && (
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-6">
            <h3 className="text-lg font-semibold mb-4">Configurações do Instagram Business</h3>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Access Token
                </label>
                <input
                  type="password"
                  value={config.accessToken}
                  onChange={(e) => setConfig({...config, accessToken: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="EAABw..."
                  disabled={testMode}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Business Account ID
                </label>
                <input
                  type="text"
                  value={config.businessAccountId}
                  onChange={(e) => setConfig({...config, businessAccountId: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="17841400000000000"
                  disabled={testMode}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Verify Token
                </label>
                <input
                  type="text"
                  value={config.verifyToken}
                  onChange={(e) => setConfig({...config, verifyToken: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Chatbot URL
                </label>
                <input
                  type="text"
                  value={config.chatbotUrl}
                  onChange={(e) => setConfig({...config, chatbotUrl: e.target.value})}
                  className="w-full px-3 py-2 border rounded-lg"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={saveConfig}
                className="px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600"
              >
                Salvar Configurações
              </button>
              <button
                onClick={testWebhook}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Testar Webhook
              </button>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          {/* Messages Section */}
          <div className="col-span-2 bg-white rounded-2xl shadow-lg p-6">
            <h2 className="text-xl font-semibold mb-4 flex items-center gap-2">
              <MessageCircle size={24} />
              Mensagens e Comentários
            </h2>
            
            {/* Messages List */}
            <div className="h-96 overflow-y-auto border rounded-lg p-4 mb-4 bg-gray-50">
              {messages.map((msg) => (
                <div key={msg.id} className={`mb-4 ${msg.type === 'sent' ? 'text-right' : 'text-left'}`}>
                  <div className={`inline-block max-w-xs ${
                    msg.type === 'sent' 
                      ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white' 
                      : msg.type === 'comment'
                      ? 'bg-yellow-100 text-gray-800'
                      : 'bg-gray-200 text-gray-800'
                  } rounded-lg px-4 py-2`}>
                    <div className="text-xs opacity-75 mb-1">
                      {msg.sender} • {msg.platform === 'comment' ? '💬 Comentário' : msg.platform === 'story' ? '📸 Story' : '✉️ DM'}
                    </div>
                    <div className="whitespace-pre-wrap">{msg.content}</div>
                    <div className="text-xs opacity-75 mt-1">
                      {msg.timestamp.toLocaleTimeString('pt-BR')}
                    </div>
                  </div>
                </div>
              ))}
              {isTyping && (
                <div className="text-left">
                  <div className="inline-block bg-gray-200 rounded-lg px-4 py-2">
                    <div className="flex gap-1">
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" />
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.1s'}} />
                      <span className="w-2 h-2 bg-gray-500 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                placeholder={language === 'pt' ? "Digite sua mensagem..." : "Type your message..."}
                className="flex-1 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <button
                onClick={sendMessage}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 text-white rounded-lg hover:shadow-lg transition-shadow"
              >
                <Send size={20} />
              </button>
            </div>

            {/* Quick Actions */}
            <div className="flex gap-2 mt-4">
              <button
                onClick={simulateIncoming}
                className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
              >
                Simular Mensagem Recebida
              </button>
              <button
                onClick={() => setMessages([])}
                className="px-4 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600"
              >
                Limpar Histórico
              </button>
            </div>
          </div>

          {/* Templates & Info Section */}
          <div className="space-y-6">
            {/* Templates */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
                <Bot size={20} />
                Templates Rápidos
              </h3>
              <div className="space-y-2">
                <button
                  onClick={() => sendTemplate('welcome')}
                  className="w-full text-left px-4 py-2 bg-purple-50 rounded-lg hover:bg-purple-100"
                >
                  <div className="font-medium">Boas-vindas</div>
                  <div className="text-sm text-gray-600">Mensagem de boas-vindas padrão</div>
                </button>
                <button
                  onClick={() => sendTemplate('availability')}
                  className="w-full text-left px-4 py-2 bg-blue-50 rounded-lg hover:bg-blue-100"
                >
                  <div className="font-medium">Disponibilidade</div>
                  <div className="text-sm text-gray-600">Redirecionar para chat</div>
                </button>
                <button
                  onClick={() => sendTemplate('location')}
                  className="w-full text-left px-4 py-2 bg-green-50 rounded-lg hover:bg-green-100"
                >
                  <div className="font-medium">Localização</div>
                  <div className="text-sm text-gray-600">Informações sobre o local</div>
                </button>
                <button
                  onClick={() => sendTemplate('amenities')}
                  className="w-full text-left px-4 py-2 bg-yellow-50 rounded-lg hover:bg-yellow-100"
                >
                  <div className="font-medium">Comodidades</div>
                  <div className="text-sm text-gray-600">O que oferecemos</div>
                </button>
                <button
                  onClick={() => sendTemplate('booking')}
                  className="w-full text-left px-4 py-2 bg-pink-50 rounded-lg hover:bg-pink-100"
                >
                  <div className="font-medium">Reservas</div>
                  <div className="text-sm text-gray-600">Como fazer reserva</div>
                </button>
              </div>
            </div>

            {/* Pousada Info */}
            <div className="bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl shadow-lg p-6 text-white">
              <h3 className="text-lg font-semibold mb-4">Carneiros Paradiso</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <MapPin size={16} />
                  <span className="text-sm">Praia dos Carneiros, PE</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar size={16} />
                  <span className="text-sm">Check-in: 14h | Check-out: 12h</span>
                </div>
                <div className="flex items-center gap-2">
                  <Users size={16} />
                  <span className="text-sm">12 Suítes Exclusivas</span>
                </div>
                <div className="flex items-center gap-2">
                  <Heart size={16} />
                  <span className="text-sm">4.9 ⭐ (250+ avaliações)</span>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm opacity-90">
                  Uma experiência única em uma das praias mais paradisíacas do Brasil
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="bg-white rounded-2xl shadow-lg p-6">
              <h3 className="text-lg font-semibold mb-4">Estatísticas</h3>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">Mensagens Hoje</span>
                  <span className="font-semibold">{messages.filter(m => m.type === 'received').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Respostas Automáticas</span>
                  <span className="font-semibold">{messages.filter(m => m.type === 'sent').length}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Taxa de Resposta</span>
                  <span className="font-semibold text-green-600">100%</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Tempo Médio</span>
                  <span className="font-semibold">&lt; 1 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};